// frontend/src/pages/HomePage.tsx

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as api from "../services/api";
import { Loader, AlertCircle, Info, ArrowRight, UserCircle } from "lucide-react";

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [checkingClient, setCheckingClient] = useState(true);
  const [eventCode, setEventCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState<"error" | "warning" | "info">("error");

  useEffect(() => {
    const checkExistingClient = async () => {
      const clientIds: string[] = [];

      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("clientId_")) {
          const clientId = localStorage.getItem(key);
          if (clientId) {
            clientIds.push(clientId);
          }
        }
      });

      if (clientIds.length > 0) {
        try {
          const clientId = clientIds[0];
          const data = await api.getClientPosition(clientId);

          if (data && data.status !== "COMPLETED" && data.status !== "CANCELLED") {
            navigate(`/status?clientId=${clientId}`, { replace: true });
            return;
          } else {
            Object.keys(localStorage).forEach((key) => {
              if (key.startsWith("clientId_") && localStorage.getItem(key) === clientId) {
                localStorage.removeItem(key);
              }
            });
          }
        } catch (err) {
          clientIds.forEach((clientId) => {
            Object.keys(localStorage).forEach((key) => {
              if (key.startsWith("clientId_") && localStorage.getItem(key) === clientId) {
                localStorage.removeItem(key);
              }
            });
          });
        }
      }
      setCheckingClient(false);
    };

    checkExistingClient();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setErrorType("error");

    try {
      const event = await api.verifyEventCode(eventCode.toUpperCase());
      navigate(`/join?eventId=${event.id}`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || t('home.invalidCode');

      if (errorMessage.toLowerCase().includes("commencé") || errorMessage.toLowerCase().includes("start")) {
        setErrorType("info");
      } else if (errorMessage.toLowerCase().includes("pause") || errorMessage.toLowerCase().includes("fermé") || errorMessage.toLowerCase().includes("closed")) {
        setErrorType("warning");
      } else {
        setErrorType("error");
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getErrorIcon = () => {
    switch (errorType) {
      case "info":
        return <Info className="w-5 h-5 flex-shrink-0" />;
      case "warning":
        return <AlertCircle className="w-5 h-5 flex-shrink-0" />;
      default:
        return <AlertCircle className="w-5 h-5 flex-shrink-0" />;
    }
  };

  const getErrorClasses = () => {
    const baseClasses = "p-4 rounded-2xl text-sm border font-medium flex items-start gap-3 animate-in fade-in slide-in-from-top-2";
    switch (errorType) {
      case "info":
        return `${baseClasses} bg-blue-50/50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800/50`;
      case "warning":
        return `${baseClasses} bg-amber-50/50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50`;
      default:
        return `${baseClasses} bg-red-50/50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50`;
    }
  };

  if (checkingClient) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex flex-col items-center justify-center p-4 transition-colors">
        <Loader className="w-10 h-10 text-blue-600 animate-spin mb-6" />
        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">{t('common.sessionResume')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex flex-col items-center justify-center p-4 sm:p-6 transition-colors overflow-hidden relative selection:bg-blue-200 selection:text-blue-900">
      
      {/* Background Decorative Rings */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/5 dark:bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="w-full max-w-md relative z-10 animate-in fade-in slide-in-from-bottom-8 duration-700">
        
        {/* Header / Brand */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-blue-600 text-white rounded-3xl mx-auto flex items-center justify-center font-black text-2xl shadow-xl shadow-blue-500/20 mb-6 rotate-[-5deg] hover:rotate-0 transition-transform">
            QY
          </div>
          <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tighter mb-2">
            {t('home.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 font-medium">{t('home.subtitle')}</p>
        </div>

        {/* Main Card */}
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl rounded-[2rem] shadow-2xl border border-white/20 dark:border-slate-800/50 p-6 sm:p-8 mb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="eventCode" className="block text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">
                {t('home.eventCodeLabel')}
              </label>
              <div className="relative group">
                <input
                  id="eventCode"
                  type="text"
                  value={eventCode}
                  onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                  placeholder={t('home.eventCodePlaceholder')}
                  required
                  maxLength={6}
                  className="w-full px-5 py-4 bg-gray-50 dark:bg-slate-950 border-2 border-gray-200 dark:border-slate-800 rounded-2xl text-center text-2xl sm:text-3xl font-black text-gray-900 dark:text-white uppercase tracking-[0.2em] focus:border-blue-600 dark:focus:border-blue-500 focus:ring-4 focus:ring-blue-600/10 placeholder:text-gray-300 dark:placeholder:text-gray-700 transition-all outline-none"
                />
              </div>
            </div>

            {error && (
              <div className={getErrorClasses()}>
                {getErrorIcon()}
                <span className="leading-relaxed flex-1">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || eventCode.length < 3}
              className="group w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black uppercase tracking-widest text-sm flex items-center justify-center gap-3 transition-all active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100 shadow-lg shadow-blue-600/25"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>{t('home.validating')}</span>
                </>
              ) : (
                <>
                  <span>{t('home.joinQueue')}</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer / Admin Access */}
        <div className="text-center">
            <button
                onClick={() => navigate("/admin/login")}
                className="inline-flex items-center justify-center gap-2 text-sm font-bold text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
                <UserCircle className="w-4 h-4" />
                {t('home.proAccess')}
            </button>
        </div>

      </div>
    </div>
  );
}
